import React, { useEffect, useState } from "react";
import { Box, Button, Modal, Tabs, Tab } from "@mui/material";
import {
  Button as Btn,
  Card,
  CardContent,
  Typography,
} from "@material-ui/core";
import { DarkGrey, PrimaryColor } from "../../shared/styles/colors";
import { Spinner } from "../../components";
import { BlobProvider } from "@react-pdf/renderer";
import { useSearchParams } from "react-router-dom";
import DB_Status from "../../model/enum/DBStatus";
import EstimateModal from "./EstimateModal";
import DateConverter from "../../utils/DateConverter";
import ApiService from "../../service/ApiService";
import EmailService from "../../service/EmailService";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import StagedPayment from "../../model/stagedPayment";
import BundleItem from "../../model/bundleItem";
import Estimate from "../../model/estimate";
import EstimatePreview from "./EstimatePreview";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ProductDetailModal from "../checklist/product/ProductDetailModal";
import UpdateStatusModal from "./UpdateStatusModal";
import BundleDetailModal from "../checklist/bundle/BundleDetailModal";
import LogoIcon from "../../assets/main.png";
import InfoIcon from "@mui/icons-material/Info";
import ShareIcon from "@mui/icons-material/Share";
import CheckIcon from "@mui/icons-material/Check";
import Attachment from "../../model/attachment";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/ToastMessages";
import Misc from "../../utils/Misc";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "5%", // Start from a fixed top position
  left: "50%",
  transform: "translateX(-50%)", // Only horizontally center the modal
  width: "90% !important",
  maxWidth: "85rem",
  bgcolor: "#F9F9F9",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  estimate: Estimate;
  resetList?: () => void;
  externalButton?: boolean;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const EstimateDetailModal = ({
  estimate,
  resetList,
  externalButton = false,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0); // Active tab
  const [downPayment, setDownPayment] = useState(false);
  const [stagedPayments, setStagedPayments] = useState<StagedPayment[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleTabChange = (e: any, newValue: number) => {
    setValue(newValue);
    setPdfUrl(null); // Reset PDF URL when fetching new data
  };

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(estimate.code);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const estimateCode = searchParams.get("estimateCode");
    if (!open && estimateCode && estimateCode === estimate.code) {
      handleOpen();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [fetchStagedPayments, fetchAttachments] = await Promise.all([
          ApiService.get<StagedPayment[]>(
            `${DatabaseEntities.STAGED_PAYMENTS}/${estimate.code}`
          ),
          ApiService.get<Attachment[]>(
            `${DatabaseEntities.ESTIMATES}/${DatabaseEntities.ATTACHMENTS}/${estimate.code}`
          ),
        ]);
        setStagedPayments(fetchStagedPayments);
        if (fetchStagedPayments.length > 0) {
          setDownPayment(true);
        }
        if (fetchAttachments.length > 0) {
          estimate.attachments = fetchAttachments;
        }
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
        console.error("Error fetching staged payments or attachments:", error);
      } finally {
        setLoading(false);
        setPdfUrl(null); // Reset PDF URL when fetching new data
      }
    };

    if (open) fetchData();
  }, [open]);

  const copyToClipboard = async () => {
    try {
      const link = `${window.location.host}/estimate/preview?code=${estimate.code}&token=${estimate.previewToken}`;
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleEmailSend = async () => {
    try {
      if (estimate.Client)
        await EmailService.sendEmailToUser(
          estimate.Client.firstName,
          estimate.Client.email,
          "Estimate",
          undefined,
          undefined,
          undefined,
          estimate.notes,
          estimate,
          estimate.attachments
        );
      AlertService.success(ToastMessages.EMAIL_SENT_TO_CLIENT);
    } catch (error) {
      AlertService.error(`${ToastMessages.EMAIL_NOT_SENT_TO_CLIENT}: ${error}`);
    }
  };

  return (
    <div>
      <Button onClick={handleOpen}>
        {!externalButton ? (
          <MoreVertIcon sx={{ color: PrimaryColor }} />
        ) : (
          <InfoIcon style={{ color: PrimaryColor }} />
        )}
      </Button>
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Box sx={style}>
              <Card style={{ padding: "25px" }}>
                <div className={classes.headerDiv}>
                  <Typography
                    variant="subtitle1"
                    style={{
                      color: DarkGrey,
                      textAlign: "center",
                      width: "auto",
                      fontSize: "1.1rem",
                    }}
                  >
                    Estimate
                  </Typography>
                  <div className={classes.headerSubDiv}>
                    <EstimateModal estimate={estimate} resetList={resetList} />
                    <Button onClick={handleClose} sx={{ color: "black" }}>
                      <CloseIcon />
                    </Button>
                  </div>
                </div>
                <hr />
                <div className={classes.headerDiv}>
                  <Box
                    sx={{
                      width: "20%",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Tabs
                      value={value}
                      onChange={handleTabChange}
                      centered
                      variant="standard"
                      textColor="inherit"
                      indicatorColor="primary"
                      TabIndicatorProps={{
                        sx: { height: 2, bgcolor: PrimaryColor }, // Customize the underline
                      }}
                    >
                      <Tab
                        label="Details"
                        sx={{
                          opacity: value === 0 ? 1 : 0.5,
                          textTransform: "capitalize",
                        }}
                      />
                      <Tab
                        label="Preview"
                        sx={{
                          opacity: value === 1 ? 1 : 0.5,
                          textTransform: "capitalize",
                        }}
                      />
                    </Tabs>
                  </Box>
                  <Btn
                    style={{ textTransform: "none" }}
                    onClick={copyToClipboard}
                  >
                    {!isCopied ? (
                      <>
                        <ShareIcon style={{ color: DarkGrey }} />
                        Share
                      </>
                    ) : (
                      <>
                        <CheckIcon style={{ color: DarkGrey }} />
                        Copied
                      </>
                    )}
                  </Btn>
                </div>
              </Card>
              {value === 0 ? (
                <div className={classes.form}>
                  <Card style={{ width: "80%", padding: "5px" }}>
                    <CardContent>
                      <div className={classes.headerDiv}>
                        <div className="d-flex col align-items-center">
                          <Typography
                            style={{
                              color: PrimaryColor,
                              fontSize: "2rem",
                              fontWeight: "700",
                              textTransform: "uppercase",
                              marginRight: "10px",
                            }}
                          >
                            Estimate
                          </Typography>
                          <Typography
                            style={{ color: PrimaryColor }}
                          >{`#${estimate.code}`}</Typography>
                        </div>
                        <img
                          src={LogoIcon}
                          alt="logo"
                          width={120}
                          height={50}
                        />
                      </div>
                      <div className="d-flex col w-100 mt-2">
                        <div className={classes.infoDiv}>
                          <Typography>Setup Remodeling Inc.</Typography>
                          <Typography>72 Cash St.</Typography>
                          <Typography>Fall River Ma 02723</Typography>
                        </div>
                        <div className={classes.infoDiv}>
                          <Typography>sales@setupremodeling.com</Typography>
                          <Typography>+1 (800)-530-4099</Typography>
                          <Typography>wwww.setupremodeling.com</Typography>
                        </div>
                        {estimate.Client && (
                          <div className={classes.infoDiv}>
                            <Typography>Bill to:</Typography>
                            <Typography>
                              {`${estimate.Client.firstName} ${estimate.Client.lastName}`}
                            </Typography>
                            <Typography>{estimate.Client.email}</Typography>
                            <Typography>
                              {estimate.Client?.street && (
                                <span>{estimate.Client.street}</span>
                              )}
                              {estimate.Client?.city && (
                                <span>, {estimate.Client.city}</span>
                              )}
                              {estimate.Client?.state && (
                                <span>, {estimate.Client.state}</span>
                              )}
                            </Typography>
                          </div>
                        )}
                      </div>
                      <hr />
                      <div className="table-container">
                        <table className="styled-table">
                          <thead style={{ color: "rgba(117, 117, 117, 1)" }}>
                            <td>Task</td>
                            <td>Description</td>
                            <td>Quantity</td>
                            <td>Rate</td>
                            <td>Total</td>
                            <td>
                              <MoreVertIcon sx={{ color: PrimaryColor }} />
                            </td>
                          </thead>
                          <tbody>
                            {estimate?.items?.map((row) => (
                              <React.Fragment key={row.id}>
                                {/* Parent Row */}
                                <tr key={row.id}>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.task}
                                      disabled
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      value={row.description}
                                      disabled
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      value={row.quantity}
                                      disabled
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      value={row.rate}
                                      disabled
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      value={row.amount}
                                      disabled
                                    />
                                  </td>
                                  <td>
                                    <BundleDetailModal bundle={row} />
                                  </td>
                                </tr>
                                {/* Child Rows */}
                                <>
                                  {row.BundleItems.map((child: BundleItem) => (
                                    <tr key={child.id}>
                                      <td
                                        style={{
                                          paddingLeft: "30px",
                                        }}
                                      >
                                        <input
                                          style={{
                                            background: "#F1F2F3",
                                            color: "grey",
                                          }}
                                          type="text"
                                          placeholder="Select task"
                                          value={child.Product.name}
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <input
                                          style={{
                                            background: "#F1F2F3",
                                            color: "grey",
                                          }}
                                          type="text"
                                          placeholder="Add a description.."
                                          value={child.Product.description}
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <input
                                          style={{
                                            background: "#F1F2F3",
                                            color: "grey",
                                          }}
                                          type="number"
                                          placeholder="01"
                                          min={1}
                                          value={child.quantity}
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <input
                                          style={{
                                            background: "#F1F2F3",
                                            color: "grey",
                                          }}
                                          type="number"
                                          placeholder="01"
                                          min={1}
                                          value={child.Product.price}
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <input
                                          style={{
                                            background: "#F1F2F3",
                                            color: "grey",
                                          }}
                                          type="number"
                                          placeholder="01"
                                          min={1}
                                          value={
                                            child.Product.price * child.quantity
                                          }
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <ProductDetailModal
                                          product={child.Product}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* <div className="d-flex col w-100 mt-2 align-items-center justify-content-end">
                        <Typography>Total Amount:</Typography>
                        <Typography
                          style={{
                            color: PrimaryColor,
                            fontSize: "20px",
                            marginLeft: "10px",
                          }}
                        >
                          ${estimate.total},00
                        </Typography>
                      </div> */}
                      {estimate.discount > 0 && (
                        <div className="d-flex col w-100 mt-2 align-items-center justify-content-end">
                          <Typography>Discount:</Typography>
                          <Typography
                            style={{
                              color: PrimaryColor,
                              fontSize: "17px",
                              marginLeft: "10px",
                            }}
                          >
                            {estimate.discount}%
                          </Typography>
                        </div>
                      )}
                      <div className="d-flex row mt-5 align-items-center">
                        <Typography>Notes to costumer</Typography>
                        <textarea
                          className={classes.textareaDetail}
                          value={estimate.notes}
                          disabled
                        />
                      </div>
                      <div className="d-flex row mt-2 align-items-center">
                        <Typography>Attachments</Typography>
                        {estimate.attachments &&
                        estimate.attachments.length > 0 ? (
                          <div className={classes.attachmentsDiv}>
                            {estimate?.attachments.map((attachment) => (
                              <a
                                key={attachment.id}
                                href={attachment.filePath}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {attachment.fileName}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            No attachments
                          </Typography>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  {/** Details */}
                  <div className={classes.detailsDiv}>
                    <Card>
                      <CardContent>
                        <Typography
                          style={{
                            color: PrimaryColor,
                            fontSize: "20px",
                            fontWeight: 600,
                          }}
                        >
                          Details
                        </Typography>
                        <div className="d-flex col mt-4 justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Estimate total:
                          </Typography>
                          <Typography>{Misc.toUSD(estimate.total)}</Typography>
                        </div>
                        <div className="d-flex col justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Due date:
                          </Typography>
                          <Typography>
                            {DateConverter.toCompleteDate(estimate.dueDate)}
                          </Typography>
                        </div>
                        <div className="d-flex col justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Status:
                          </Typography>
                          <div style={{ width: "50%" }}>
                            <Typography
                              style={DB_Status.mapStatusColor(
                                estimate.Status?.name
                              )}
                            >
                              <div
                                style={DB_Status.mapStatusRoundedColor(
                                  estimate.Status?.name
                                )}
                              ></div>
                              {estimate.Status?.name}
                            </Typography>
                          </div>
                        </div>
                        <div className="d-flex col justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Created At:
                          </Typography>
                          <Typography>
                            {DateConverter.toFullDate(estimate.createdAt)}
                          </Typography>
                        </div>
                        {estimate.acceptedBy && (
                          <div className="d-flex col justify-content-between align-items-center">
                            <Typography
                              style={{ color: "grey", fontSize: "15px" }}
                            >
                              Accepted By:
                            </Typography>
                            <Typography>{estimate.acceptedBy}</Typography>
                          </div>
                        )}
                        {estimate.acceptedAt && (
                          <div className="d-flex col justify-content-between align-items-center">
                            <Typography
                              style={{ color: "grey", fontSize: "15px" }}
                            >
                              Accepted At:
                            </Typography>
                            <Typography>
                              {DateConverter.toFullDate(estimate.acceptedAt)}
                            </Typography>
                          </div>
                        )}
                        <hr />
                        <div className="d-flex col justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Urgent payment:
                          </Typography>
                          {estimate.urgentPayment ? (
                            <ToggleOnIcon />
                          ) : (
                            <ToggleOffIcon />
                          )}
                        </div>
                        <div className="d-flex col justify-content-between align-items-center">
                          <Typography
                            style={{ color: "grey", fontSize: "15px" }}
                          >
                            Downpayment:
                          </Typography>
                          {downPayment ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </div>
                        {stagedPayments.length > 0 && (
                          <div className="d-flex col justify-content-between align-items-center">
                            <Typography
                              style={{ color: "grey", fontSize: "15px" }}
                            >
                              Payed:
                            </Typography>
                            {stagedPayments.map((payment) => (
                              <label key={payment.id}>
                                {`$${payment.amount},00 - ${payment.PaymentMethod?.name}`}
                              </label>
                            ))}
                          </div>
                        )}
                        <hr />
                        {/** TODO: send estimate and convert to invoice */}
                        <div>
                          <UpdateStatusModal estimate={estimate} />
                          <Btn
                            className={classes.button}
                            style={{ width: "100%" }}
                            onClick={handleEmailSend}
                          >
                            Send estimate
                          </Btn>
                          <Btn
                            className={classes.externalButton}
                            style={{ width: "100%" }}
                            disabled
                          >
                            Convert to invoice
                          </Btn>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <Card
                  style={{ width: "100%", padding: "25px", marginTop: "1rem" }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      width: "100%",
                      height: "600px",
                    }}
                  >
                    {!pdfUrl ? (
                      <BlobProvider
                        document={<EstimatePreview estimate={estimate} />}
                      >
                        {({ url, loading }) => {
                          if (loading) return <Spinner />;
                          setPdfUrl(url);
                          return null; // Return null since we don't need to render anything here
                        }}
                      </BlobProvider>
                    ) : (
                      <>
                        <iframe
                          src={pdfUrl}
                          style={{ width: "100%", height: "500px" }}
                        />
                        <div className={classes.buttonDiv}>
                          <Btn
                            className={classes.button}
                            href={pdfUrl}
                            download={`estimate_${estimate.code}.pdf`}
                            disabled={!pdfUrl}
                          >
                            Download PDF
                          </Btn>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              )}
            </Box>
          )}
        </Modal>
      )}
    </div>
  );
};

export default EstimateDetailModal;
